import React from "react";
import { fmtNum, fmtPct } from "../../utils/format";
import styles from "./ProductFunnels.module.css";

// Продуктовые воронки: общая плашка TikTok сверху, ниже три воронки
// (Рублокс / Майнкрафтия / Маська), каждая — переходы -> регистрации -> активные.
// Конверсии между ступенями рисуются скобками справа (как в классической
// маркетинговой воронке).

const STAGE_COLORS = { visits: "#4f7cff", regs: "#39c5cf", active: "#3fb950", pays: "#d29922" };

// Геометрия одной колонки
const W = 380;          // ширина viewBox колонки
const ROW_H = 62;       // высота ступени
const GAP = 30;         // зазор между ступенями
const TOP_W = 264;      // ширина верхней ступени
const BOT_W = 148;      // ширина нижней ступени
const CX = 152;         // центр ступеней (смещён влево — справа место для скобок)
const BRACKET_X = 296;  // x вертикали скобки

function widthAt(i, n) {
  return TOP_W - (TOP_W - BOT_W) * (i / Math.max(1, n - 1));
}

function StageRect({ stage, x, y, w }) {
  const noData = stage.count == null;
  const color = STAGE_COLORS[stage.key] || "#4f7cff";
  return (
    <g>
      <rect
        x={x} y={y} width={w} height={ROW_H} rx={9}
        fill={noData ? "transparent" : color}
        fillOpacity={noData ? 0 : stage.estimated ? 0.45 : 0.92}
        stroke={noData || stage.estimated ? "#4a5568" : "none"}
        strokeWidth={noData || stage.estimated ? 1.4 : 0}
        strokeDasharray={noData || stage.estimated ? "5 4" : "0"}
      />
      <text x={CX} y={y + ROW_H / 2 - 8} textAnchor="middle"
            className={styles.stageTitle} fill={noData ? "#8b98a9" : "#fff"}>
        {stage.title}
      </text>
      <text x={CX} y={y + ROW_H / 2 + 15} textAnchor="middle"
            className={styles.stageCount} fill={noData ? "#5c6b7e" : "#fff"}>
        {noData ? "нет данных" : (stage.estimated ? "≈ " : "") + fmtNum(stage.count)}
      </text>
    </g>
  );
}

// Скобка конверсии между ступенями i-1 и i: вертикаль с засечками к серединам
// обеих ступеней + подпись процента справа.
function ConvBracket({ yTop, yBottom, conv }) {
  const midY = (yTop + yBottom) / 2;
  return (
    <g>
      <path
        d={`M ${BRACKET_X - 8} ${yTop} H ${BRACKET_X} V ${yBottom} H ${BRACKET_X - 8}`}
        fill="none" stroke="#5c6b7e" strokeWidth="1.3"
      />
      <text x={BRACKET_X + 9} y={midY - 3} className={styles.convPct}>
        {fmtPct(conv * 100)}
      </text>
      <text x={BRACKET_X + 9} y={midY + 12} className={styles.convLabel}>
        конверсия
      </text>
    </g>
  );
}

function ProductColumn({ product }) {
  const n = product.stages.length;
  const H = n * ROW_H + (n - 1) * GAP;
  return (
    <div className={styles.col}>
      <div className={styles.colTitle}>{product.title}</div>
      <svg viewBox={`0 0 ${W} ${H + 4}`} className={styles.colSvg} preserveAspectRatio="xMidYMin meet">
        {product.stages.map((s, i) => {
          const y = i * (ROW_H + GAP) + 2;
          const w = widthAt(i, n);
          return (
            <g key={s.key}>
              {i > 0 && (
                <line
                  x1={CX - widthAt(i - 1, n) / 2} y1={y - GAP / 2}
                  x2={CX + widthAt(i - 1, n) / 2} y2={y - GAP / 2}
                  stroke="#2a3444" strokeWidth="1" strokeDasharray="5 5"
                />
              )}
              <StageRect stage={s} x={CX - w / 2} y={y} w={w} />
              {/* конверсию свыше 1000% не рисуем — ступени несопоставимы
                  (например, счётчик визитов заработал позже периода) */}
              {s.conv_from_prev != null && s.conv_from_prev <= 10 && (
                <ConvBracket
                  yTop={y - GAP - ROW_H / 2}
                  yBottom={y + ROW_H / 2}
                  conv={s.conv_from_prev}
                />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function ProductFunnels({ tiktok, products }) {
  if (!products || products.length === 0) {
    return <div className={styles.empty}>Нет данных воронки. Запусти сбор данных.</div>;
  }
  const hasEstimate = products.some((p) => p.stages.some((s) => s.estimated));
  const hasNoData = products.some((p) => p.stages.some((s) => s.count == null));
  const hasOverflow = products.some(
    (p) => p.stages.some((s) => s.conv_from_prev > 1 && s.conv_from_prev <= 10)
  );

  return (
    <div className={styles.wrap}>
      {tiktok && (
        <>
          <div className={styles.tiktokPlate}>
            <div className={styles.tiktokTitle}>TikTok — просмотры</div>
            <div className={styles.tiktokCount}>
              {tiktok.has_data ? fmtNum(tiktok.views) : "по неделям не считается"}
            </div>
          </div>
          <div className={styles.tiktokLink}>
            <svg viewBox="0 0 24 30" className={styles.linkArrow} aria-hidden="true">
              <path d="M12 0 V22 M5 16 L12 23 L19 16" fill="none"
                    stroke="#5c6b7e" strokeWidth="1.6" strokeDasharray="4 3" />
            </svg>
            <div className={styles.tiktokConvText}>
              переходы на сайты всего <b>{fmtNum(tiktok.visits_total)}</b>
              {tiktok.has_data && tiktok.conv_to_visits != null && (
                <span className={styles.tiktokConvPct}> · конверсия {fmtPct(tiktok.conv_to_visits * 100)}</span>
              )}
            </div>
          </div>
        </>
      )}

      <div className={styles.grid}>
        {products.map((p) => <ProductColumn key={p.key} product={p} />)}
      </div>

      {(hasEstimate || hasNoData || hasOverflow) && (
        <div className={styles.footnotes}>
          {hasEstimate && (
            <div>≈ — оценка по первой активности: точные даты регистрации до мая не сохранились.</div>
          )}
          {hasNoData && (
            <div>«Нет данных» — счётчик посещаемости ещё не работал в этот период.</div>
          )}
          {hasOverflow && (
            <div>Конверсия больше 100% — на ступени есть пользователи, пришедшие раньше этого периода.</div>
          )}
        </div>
      )}
    </div>
  );
}
